"use client";

import { Button } from "@/components/ui/button";
import { useState, useTransition } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from 'lucide-react';
import { useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useDispatch } from 'react-redux';
import { setOfflineMode } from '@/redux/slices/offline/offlineSlice';
import oktaService from "@/services/OktaService";
import { clearAllCaches } from "@/utils/cacheUtils";
import { disableMockMode } from "@/services/mockAuth";
import { isLocalAuthEnabled, localLogin } from "@/services/localAuth";
import { useLoginMutation } from "@/redux/slices/auth/authApiSlice";
import { Input } from "@/components/ui/input";
import { BadgeCheck } from "lucide-react";

const Login = () => {
    const [isPending, startTransition] = useTransition();
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const showLocalLogin = isLocalAuthEnabled();
    const [login] = useLoginMutation();
    const [localEmail, setLocalEmail] = useState("");
    const [localPassword, setLocalPassword] = useState("");
    const [isLocalPending, setIsLocalPending] = useState(false);

    // Clear caches if forceClear parameter is present
    useEffect(() => {
        const forceClear = searchParams.get('forceClear');
        if (forceClear === 'true') {
            clearAllCaches().then(() => {
                console.log('Cache cleared on login page load');
            });
        }
    }, [searchParams]);

    // Redirect to home if already authenticated
    useEffect(() => {
        // Only redirect if authenticated and NOT in offline mode
        if (user && isAuthenticated && !searchParams.get('offlineMode')) {
            navigate("/");
        }
    }, [user, isAuthenticated, navigate, searchParams]);

    // Handle Okta login
    const handleOktaLogin = () => {
        startTransition(async () => {
            try {
                disableMockMode();
                // Clear caches before login to ensure fresh state
                await clearAllCaches();
                await oktaService.login();
            } catch (error) {
                console.error('Okta login error:', error);
                toast.error('Failed to initiate PepsiCo SSO login');
            }
        });
    };

    // Handle offline mode
    const handleOfflineMode = () => {
        dispatch(setOfflineMode(true));
        navigate('/offline/projects');
    };

    const handleLocalLogin = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLocalPending(true);

        try {
            disableMockMode();
            await clearAllCaches();

            const localUser = localLogin(localEmail, localPassword);
            if (!localUser) {
                toast.error('Invalid local credentials');
                return;
            }

            await login({ oktaToken: localUser.access_token }).unwrap();

            toast(
                <div className="bg-states-success flex-center justify-start gap-2 w-[300px] p-3 rounded-md text-white">
                    <BadgeCheck />
                    <p className="semi-bold-16">Logged In Successfully</p>
                </div>
            );
            navigate('/');
        } catch (error) {
            console.error('Local login error:', error);
            toast.error('Failed to complete local login');
        } finally {
            setIsLocalPending(false);
        }
    };

    return (
        <section className="login min-w-[300px] w-full h-screen overflow-scroll flex-center bg-white sm:bg-bgBlue">
            <div className="px-5 py-10 sm:py-0 sm:px-0 sm:flex-col-center rounded-xl bg-white w-full sm:mx-5 max-w-[500px] min-h-[480px] sm:h-screen sm:max-h-[560px] relative">
                <div className="rounded-full size-[60px] sm:size-[70px] border border-white text-center sm:absolute top-[-40px] left-1/2 transform sm:-translate-x-1/2 flex-center bg-[#F4F4F4]">
                    <img
                        className="size-full border-[8px] border-white rounded-full"
                        src={'/pepsico.svg'}
                        alt="Logo"
                    />
                </div>

                <div className="bg-white sm:bg-states-disabled rounded-t-xl w-full h-[15vh] sm:h-[112px] mt-5 sm:mt-0 flex-col-center">
                    <h1 className="semi-bold-16 sm:semi-bold-24 text-left text-black sm:text-center">
                        Ship And Stack Testings
                    </h1>
                </div>

                <div className="w-full sm:w-[75%] sm:h-[calc(100%-112px)] space-y-[20px] sm:flex-col-center sm:space-y-[24px] mt-6">
                    <div className="w-full">
                        <h1 className="semi-bold-24 text-left sm:text-center text-blue capitalize">
                            PepsiCo SSO Login
                        </h1>
                        <p className="regular-14 text-left sm:text-center">
                            Sign in with your PepsiCo credentials
                        </p>
                    </div>

                    <Button
                        onClick={handleOktaLogin}
                        disabled={isPending}
                        className="w-full flex items-center justify-center bg-blue hover:bg-[#00336C] font-semibold text-lg"
                    >
                        {isPending ? (
                            <span className="flex items-center justify-center">
                                <Loader2 className="animate-spin mr-2" />
                                Connecting to PepsiCo SSO...
                            </span>
                        ) : (
                            "Sign in with PepsiCo SSO"
                        )}
                    </Button>

                    {showLocalLogin && (
                        <form className="w-full space-y-3" onSubmit={handleLocalLogin}>
                            <div className="space-y-2">
                                <label htmlFor="local-email" className="text-sm font-medium text-[#414042]">
                                    Local Email
                                </label>
                                <Input
                                    id="local-email"
                                    type="email"
                                    autoComplete="username"
                                    value={localEmail}
                                    onChange={(event) => setLocalEmail(event.target.value)}
                                    placeholder="local@local.test"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="local-password" className="text-sm font-medium text-[#414042]">
                                    Local Password
                                </label>
                                <Input
                                    id="local-password"
                                    type="password"
                                    autoComplete="current-password"
                                    value={localPassword}
                                    onChange={(event) => setLocalPassword(event.target.value)}
                                    placeholder="Local password"
                                    required
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={isLocalPending}
                                variant="outline"
                                className="w-full border-blue text-blue hover:bg-[#EAF1FF]"
                            >
                                {isLocalPending ? (
                                    <span className="flex items-center justify-center">
                                        <Loader2 className="animate-spin mr-2" />
                                        Signing in locally...
                                    </span>
                                ) : (
                                    "Sign in locally"
                                )}
                            </Button>
                        </form>
                    )}

                    <div className="text-center">
                        <p className="text-sm text-gray-500 mb-4">or</p>
                        <Button
                            onClick={handleOfflineMode}
                            variant="outline"
                            className="w-full"
                        >
                            Continue in Offline Mode
                        </Button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Login;
